import { AppState, HUMAN, humanAction, leaveToLobby, nextHand, tableOf, orderedHand, setHandOrder, toggleTutor } from '@/store/gameStore';
import { soundToggle, musicToggle } from './lobby';
import { buildHandRow, TileMarker, decorateRecommend } from './handDrag';
import { bestGroups, groupTiles } from '@/engine/groups';
import { GameState } from '@/engine/game/types';
import { legalActions } from '@/engine/game/legal';
import { Tile, tileName } from '@/engine/tiles';
import { renderTile } from './tiles';
import { doraFromIndicator } from '@/engine/dora';
import { bestDiscard, DiscardEval } from '@/engine/ai/ukeire';
import { waits } from '@/engine/waits';
import { tileFace } from './tiles';
import { fmt, fmtSigned } from './format';

const WIND = ['東', '南', '西', '北'];

export function renderTable(state: AppState): HTMLElement {
  const root = document.createElement('div');
  const g = state.game!;
  const table = tableOf();

  // topbar (slim, in-game)
  const bar = document.createElement('div');
  bar.className = 'topbar';
  const back = document.createElement('button');
  back.className = 'btn btn--ghost';
  back.textContent = '← 나가기';
  back.onclick = leaveToLobby;
  bar.appendChild(back);
  const right = document.createElement('div');
  right.style.cssText = 'display:flex;align-items:center;gap:10px';
  right.appendChild(musicToggle());
  right.appendChild(soundToggle());
  right.appendChild(tutorToggle(state));
  const info = document.createElement('div');
  info.className = 'wallet-chip';
  info.innerHTML = `<span class="label">${table?.name ?? ''} · 보유</span> ${fmt(state.wallet.balance)}`;
  right.appendChild(info);
  bar.appendChild(right);
  root.appendChild(bar);

  const screen = document.createElement('div');
  screen.className = 'screen';
  root.appendChild(screen);

  const board = document.createElement('div');
  board.className = 'board';
  screen.appendChild(board);

  // 종료 시 상대 패 공개
  const ended = g.phase === 'ended';
  const winner =
    g.result && (g.result.type === 'tsumo' || g.result.type === 'ron') ? g.result.winner : undefined;

  // 중앙 탁
  const center = document.createElement('div');
  center.className = 'board__center';
  center.appendChild(seatView(g, 2, 'top', ended, winner));
  center.appendChild(seatView(g, 3, 'left', ended, winner));
  center.appendChild(centerInfo(g));
  center.appendChild(seatView(g, 1, 'right', ended, winner));
  center.appendChild(seatView(g, 0, 'self', ended, winner));
  board.appendChild(center);

  // 손패 + 조작 + 튜터
  board.appendChild(handZone(g, state));

  // 전체 화면 연출(리치/화료 등)
  if (state.flash) {
    const flash = document.createElement('div');
    flash.className = 'riichi-flash';
    const span = document.createElement('span');
    span.textContent = state.flash;
    flash.appendChild(span);
    root.appendChild(flash);
  }

  return root;
}

function seatView(
  g: GameState,
  seat: number,
  pos: 'top' | 'left' | 'right' | 'self',
  ended: boolean,
  winner: number | undefined,
): HTMLElement {
  const p = g.players[seat];
  const el = document.createElement('div');
  el.className = `seat seat--${pos}`;
  if (ended && seat === winner) el.classList.add('seat--winner');

  const name = document.createElement('div');
  name.className = 'seat__name' + (g.turn === seat && g.phase !== 'ended' ? ' turn' : '');
  const who = seat === HUMAN ? '나' : ['', '하단', '오른쪽', '맞은편', '왼쪽'][((seat - HUMAN + 4) % 4) + 1];
  name.innerHTML = `<span class="wind">${WIND[p.seat - 27]}</span>${who}`;
  if (ended && seat === winner) {
    const badge = document.createElement('span');
    badge.className = 'winner-badge';
    badge.textContent = '화료';
    name.appendChild(badge);
  }
  el.appendChild(name);

  const pts = document.createElement('div');
  pts.className = 'seat__pts';
  pts.textContent = fmt(p.points);
  el.appendChild(pts);

  if (p.riichi) {
    const r = document.createElement('div');
    r.className = 'seat__riichi';
    r.textContent = 'リーチ';
    el.appendChild(r);
  }

  // 상대 손패: 평소엔 뒷면 개수, 종료 시엔 공개(왜 졌는지 확인)
  if (seat !== HUMAN) {
    const hand = document.createElement('div');
    hand.className = 'tile-row' + (ended ? ' revealed' : '');
    if (ended) {
      for (const t of p.hand) hand.appendChild(renderTile(t, { size: 'sm' }));
      // 화료자는 화료패까지 붙여 완성형 공개
      if (seat === winner && g.result?.winningTile !== undefined) {
        const wt = renderTile(g.result.winningTile, { size: 'sm', dora: true });
        wt.style.marginLeft = '7px';
        hand.appendChild(wt);
      }
    } else {
      for (let i = 0; i < p.hand.length; i++) hand.appendChild(renderTile(0, { size: 'sm', faceDown: true }));
    }
    el.appendChild(hand);
  }

  if (p.discards.length) {
    const river = document.createElement('div');
    river.className = 'river';
    p.discards.forEach((t, i) => {
      // 종료 시, 방총자의 마지막 버림패(= 론 당한 패)를 금색 강조
      const isWinDiscard = ended && seat === g.result?.loser && i === p.discards.length - 1;
      river.appendChild(renderTile(t, { size: 'sm', rotated: p.riichiIndex === i, dora: isWinDiscard }));
    });
    el.appendChild(river);
  }
  return el;
}

function centerInfo(g: GameState): HTMLElement {
  const c = document.createElement('div');
  c.className = 'center-info';
  const round = document.createElement('div');
  round.className = 'round';
  round.textContent = `${WIND[g.roundWind - 27]}장`;
  c.appendChild(round);

  const dora = document.createElement('div');
  dora.className = 'dora-mini';
  const lab = document.createElement('span');
  lab.className = 'label';
  lab.textContent = '도라';
  dora.appendChild(lab);
  for (const ind of g.doraIndicators) dora.appendChild(renderTile(doraFromIndicator(ind), { size: 'sm', dora: true }));
  c.appendChild(dora);

  const wall = document.createElement('div');
  wall.className = 'wall-count';
  wall.textContent = `남은 패 ${g.wall.length}`;
  c.appendChild(wall);
  return c;
}

function handZone(g: GameState, state: AppState): HTMLElement {
  const zone = document.createElement('div');
  zone.className = 'hand-zone';

  const acts = state.outcome ? [] : legalActions(g, HUMAN);
  const canT = acts.some((a) => a.type === 'tsumo');
  const canRon = acts.some((a) => a.type === 'ron');
  const canPass = acts.some((a) => a.type === 'pass');
  const discardTiles = new Set(acts.filter((a) => a.type === 'discard').map((a) => (a as { tile: Tile }).tile));
  const riichiTiles = new Set(acts.filter((a) => a.type === 'riichi').map((a) => (a as { tile: Tile }).tile));
  const me = g.players[HUMAN];
  const myDiscardTurn = discardTiles.size > 0 || canT;
  const drawn = g.turn === HUMAN && g.phase === 'discard' ? g.drawn : null;
  const hand14 = drawn !== null ? [...me.hand, drawn] : me.hand.slice();
  // 최선 버림은 렌더당 한 번만 계산해 튜터·리치 버튼이 공유
  const best = !state.outcome && myDiscardTurn && hand14.length === 14 ? bestDiscard(hand14) : null;

  // 튜터 (토글 on일 때만)
  if (state.showTutor) {
    zone.appendChild(tutorPanel(g, state, { canT, canRon, riichiTiles, myDiscardTurn, drawn, best }));
  }

  // 손패 — 탭=버림 / 드래그=정리 (표시 순서는 handOrder로 유지)
  const order = orderedHand(state.handOrder, me.hand);
  const markers = state.outcome ? undefined : computeMarkers(order);
  // 추천 버림패(튜터 on + 추천 존재 시). 표시 행에서 첫 일치 위치.
  const recTile = state.showTutor && best ? best.tile : undefined;
  const recIndex = recTile !== undefined ? order.indexOf(recTile) : -1;
  const hand = buildHandRow({
    tiles: order,
    discardable: state.outcome ? new Set<Tile>() : discardTiles,
    dimNonDiscardable: myDiscardTurn && !me.riichi,
    markers,
    recommendIndex: recIndex >= 0 ? recIndex : undefined,
    onDiscard: (tile) => humanAction({ type: 'discard', tile }),
    onReorder: (o) => setHandOrder(o),
  });
  if (drawn !== null) {
    const d = renderTile(drawn, {
      size: 'lg',
      onClick: !state.outcome && discardTiles.has(drawn) ? (tile) => humanAction({ type: 'discard', tile }) : undefined,
      title: `방금 가져온 패 · ${tileName(drawn)}`,
    });
    d.classList.add('drawn');
    if (recTile !== undefined && recIndex === -1 && drawn === recTile) decorateRecommend(d);
    hand.appendChild(d);
  }
  zone.appendChild(hand);

  // 자동정렬 (만·통·삭·자패 순으로 보기 좋게)
  const sortBar = document.createElement('div');
  sortBar.style.cssText = 'display:flex;justify-content:center;margin-top:-2px';
  const sortBtn = document.createElement('button');
  sortBtn.className = 'btn btn--ghost sort-btn';
  sortBtn.textContent = '↕ 자동정렬';
  sortBtn.title = '손패를 만·통·삭·자패 순으로 정렬';
  sortBtn.onclick = () => setHandOrder([]);
  sortBar.appendChild(sortBtn);
  zone.appendChild(sortBar);

  // 조작
  const controls = document.createElement('div');
  controls.className = 'controls';
  if (canT) controls.appendChild(actionBtn('쯔모!', 'btn--primary', () => humanAction({ type: 'tsumo' })));
  if (canRon) controls.appendChild(actionBtn('론!', 'btn--primary', () => humanAction({ type: 'ron', player: HUMAN })));
  if (canPass) controls.appendChild(actionBtn('넘기기', 'btn--ghost', () => humanAction({ type: 'pass', player: HUMAN })));
  if (riichiTiles.size > 0) {
    // 추천(최선) 패로 리치 선언
    const tile = best && riichiTiles.has(best.tile) ? best.tile : [...riichiTiles][0];
    controls.appendChild(
      actionBtn(`리치! (${tileName(tile)} 버림)`, 'btn--gold', () => humanAction({ type: 'riichi', tile })),
    );
  }
  zone.appendChild(controls);

  // 힌트 라인
  if (state.outcome) {
    zone.appendChild(resultCard(state));
    return zone;
  }

  const hint = document.createElement('div');
  hint.className = 'hint-line';
  if (canT) hint.innerHTML = '<strong>쯔모 화료</strong> 가능! 버튼을 누르세요.';
  else if (canRon) hint.innerHTML = '상대가 버린 패로 <strong>론 화료</strong> 가능!';
  else if (myDiscardTurn) hint.textContent = '버릴 패를 누르세요. (흐려진 패는 손에 남깁니다)';
  else if (g.phase !== 'ended') hint.textContent = '상대 차례…';
  zone.appendChild(hint);

  return zone;
}

interface TutorCtx {
  canT: boolean;
  canRon: boolean;
  riichiTiles: Set<Tile>;
  myDiscardTurn: boolean;
  drawn: Tile | null;
  best: DiscardEval | null;
}

function tutorPanel(g: GameState, state: AppState, ctx: TutorCtx): HTMLElement {
  const el = document.createElement('div');
  el.className = 'tutor';
  const title = document.createElement('div');
  title.className = 'tutor__title';
  title.textContent = '튜터';
  el.appendChild(title);

  const body = document.createElement('div');
  el.appendChild(body);

  if (state.outcome) {
    body.textContent = '한 판이 끝났어요. 결과를 확인하세요.';
    return el;
  }
  if (ctx.canT) {
    body.innerHTML = '완성형이에요! <span class="accept">쯔모</span>로 화료하세요.';
    return el;
  }
  if (ctx.canRon) {
    body.innerHTML = '방금 버려진 패로 손이 완성돼요. <span class="accept">론</span>!';
    return el;
  }
  if (!ctx.myDiscardTurn) {
    body.textContent = '상대가 두는 중입니다. 강(버린 패)을 눈여겨 보세요.';
    return el;
  }

  // 내 버림 차례 — 샹텐/우케이레 조언 (best는 handZone에서 계산해 공유)
  const best = ctx.best;
  if (!best) {
    body.textContent = '버릴 패를 고르세요.';
    return el;
  }
  const me = g.players[HUMAN];
  const hand14 = [...me.hand, ...(ctx.drawn !== null ? [ctx.drawn] : [])];
  const restAfterBest = hand14.filter((t, i) => i !== hand14.indexOf(best.tile));
  const w = waits(restAfterBest);

  if (best.shanten === 0) {
    body.innerHTML =
      `<strong>${tileName(best.tile)}</strong>을(를) 버리면 <span class="accept">텐파이</span>! ` +
      `대기: ${w.map(tileName).join(', ')}` +
      (ctx.riichiTiles.size > 0 ? ' · 리치를 걸 수 있어요.' : '');
  } else {
    body.innerHTML =
      `현재 완성까지 <strong>${best.shanten}샹텐</strong>. ` +
      `추천: <strong>${tileName(best.tile)}</strong> 버림 ` +
      `(받을 패 <span class="accept">${best.ukeire}종</span>).`;
  }
  return el;
}

/** 종료 결과 — 시야를 가리지 않는 인라인 막대. 판(공개된 상대 패)은 위에 그대로 보인다. */
function seatNameKo(seat: number): string {
  return seat === HUMAN ? '나' : ['', '하단', '오른쪽', '맞은편', '왼쪽'][((seat - HUMAN + 4) % 4) + 1];
}

/** 왜 이기고 졌는지 한 문장 설명. */
function explainResult(g: GameState, o: NonNullable<AppState['outcome']>): string {
  const r = g.result!;
  if (r.type === 'ryuukyoku') {
    const t = r.tenpaiSeats ?? [];
    if (t.length === 0) return '유국 — 아무도 텐파이하지 못해 점수 이동이 없었어요.';
    if (t.length === 4) return '유국 — 전원 텐파이라 점수 이동이 없었어요.';
    return `유국 — ${t.map(seatNameKo).join('·')}만 텐파이. 노텐인 사람이 벌부를 냈어요.`;
  }
  const yk = o.yaku.length ? o.yaku.map((y) => y.name).join('·') : '역';
  const stat = `${o.han}판 ${o.fu}부`;
  const w = seatNameKo(r.winner!);
  if (r.type === 'tsumo') {
    const who = r.winner === HUMAN ? '내가' : `${w}이`;
    return `${who} 스스로 패를 가져와 “${yk}”(${stat})으로 쯔모 화료 — 모두가 점수를 나눠 냈어요.`;
  }
  // ron
  const l = seatNameKo(r.loser!);
  const dealt = r.loser === HUMAN ? '내가 버린 패' : `${l}이 버린 패`;
  if (r.winner === HUMAN) {
    return `${dealt}로 내가 “${yk}”(${stat})을 완성해 론 화료했어요!`;
  }
  const blame = r.loser === HUMAN ? ' (내가 쏨)' : '';
  return `${dealt}로 ${w}이 “${yk}”(${stat})으로 론${blame} — 방총자가 점수를 물었어요.`;
}

/** 점수 순위(1~4위, 꼴등 표시). */
function rankingRow(g: GameState): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'ranking';
  const sorted = g.players
    .map((p, seat) => ({ seat, points: p.points }))
    .sort((a, b) => b.points - a.points);
  sorted.forEach((p, i) => {
    const item = document.createElement('div');
    item.className = 'rank-item' + (p.seat === HUMAN ? ' me' : '') + (i === 3 ? ' last' : '');
    const no = document.createElement('span');
    no.className = 'rank-no';
    no.textContent = `${i + 1}위`;
    const nm = document.createElement('span');
    nm.className = 'rank-name';
    nm.textContent = seatNameKo(p.seat);
    const pt = document.createElement('span');
    pt.className = 'rank-pts';
    pt.textContent = fmt(p.points);
    item.appendChild(no);
    item.appendChild(nm);
    item.appendChild(pt);
    if (i === 3) {
      const badge = document.createElement('span');
      badge.className = 'last-badge';
      badge.textContent = '꼴등';
      item.appendChild(badge);
    }
    wrap.appendChild(item);
  });
  return wrap;
}

function resultCard(state: AppState): HTMLElement {
  const o = state.outcome!;
  const g = state.game!;
  const r = g.result!;
  const card = document.createElement('div');
  card.className = 'result-inline result-inline--full';

  // 1행: 판정 + 화료패 + 머니
  const head = document.createElement('div');
  head.className = 'result-head';
  const v = document.createElement('div');
  if (o.kind === 'ryuukyoku') {
    v.className = 'rv draw';
    v.textContent = '유국';
  } else if (o.humanWon) {
    v.className = 'rv win';
    v.textContent = o.kind === 'tsumo' ? '쯔모!' : '론!';
  } else {
    v.className = 'rv lose';
    v.textContent = o.kind === 'tsumo' ? '상대 쯔모' : '상대 론';
  }
  head.appendChild(v);
  if (r.winningTile !== undefined) {
    const wtile = document.createElement('div');
    wtile.className = 'wtile';
    const lab = document.createElement('span');
    lab.textContent = '화료패';
    wtile.appendChild(lab);
    wtile.appendChild(renderTile(r.winningTile, { size: 'sm', dora: true }));
    head.appendChild(wtile);
  }
  const money = document.createElement('div');
  money.className = 'result-inline__money ' + (o.moneyDelta > 0 ? 'plus' : o.moneyDelta < 0 ? 'minus' : '');
  money.textContent = `${fmtSigned(o.moneyDelta)}원`;
  head.appendChild(money);
  card.appendChild(head);

  // 2행: 설명(왜 이기고 졌나)
  const expl = document.createElement('div');
  expl.className = 'result-expl';
  expl.textContent = explainResult(g, o);
  card.appendChild(expl);

  // 3행: 역(무엇으로 화료했나)
  if (o.yaku.length) {
    const yk = document.createElement('div');
    yk.className = 'result-yaku';
    for (const y of o.yaku) {
      const chip = document.createElement('span');
      chip.className = 'yaku-chip';
      chip.textContent = `${y.name} ${y.han}판`;
      yk.appendChild(chip);
    }
    const total = document.createElement('span');
    total.className = 'yaku-chip total';
    total.textContent = `합 ${o.han}판 ${o.fu}부`;
    yk.appendChild(total);
    card.appendChild(yk);
  }

  // 4행: 순위
  card.appendChild(rankingRow(g));

  // 5행: 버튼
  const btns = document.createElement('div');
  btns.className = 'result-inline__btns';
  const again = document.createElement('button');
  again.className = 'btn btn--primary';
  again.textContent = '한 판 더';
  again.onclick = nextHand;
  const lobby = document.createElement('button');
  lobby.className = 'btn btn--ghost';
  lobby.textContent = '로비로';
  lobby.onclick = leaveToLobby;
  btns.appendChild(again);
  btns.appendChild(lobby);
  card.appendChild(btns);

  return card;
}

const GROUP_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7']; // 파랑·초록·호박·보라
const PAIR_COLOR = '#94a3b8'; // 머리(대자) — 그레이

/** 표시 순서에서 인접한 완성 묶음만 위치 인덱스 마커로 변환. */
function computeMarkers(displayTiles: Tile[]): Map<number, TileMarker> {
  const { groups } = bestGroups(displayTiles);
  const markers = new Map<number, TileMarker>();
  const claimed = new Set<number>();
  let colorIdx = 0;
  for (const g of groups) {
    const want = groupTiles(g); // 오름차순
    const len = want.length;
    for (let i = 0; i + len <= displayTiles.length; i++) {
      let free = true;
      for (let k = i; k < i + len; k++) if (claimed.has(k)) { free = false; break; }
      if (!free) continue;
      const window = displayTiles.slice(i, i + len).sort((a, b) => a - b);
      if (window.every((v, j) => v === want[j])) {
        const color = g.type === 'pair' ? PAIR_COLOR : GROUP_COLORS[colorIdx++ % GROUP_COLORS.length];
        const anchor = i + Math.floor((len - 1) / 2);
        for (let k = i; k < i + len; k++) {
          claimed.add(k);
          markers.set(k, { type: g.type, color, anchor: k === anchor });
        }
        break;
      }
    }
  }
  return markers;
}

function actionBtn(label: string, variant: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = `btn ${variant}`;
  b.textContent = label;
  b.onclick = onClick;
  return b;
}

/** 튜터 on/off 토글. 음악 토글과 같은 스타일(밝게/흐리게). */
function tutorToggle(state: AppState): HTMLButtonElement {
  const b = document.createElement('button');
  b.className = 'btn btn--ghost sound-toggle';
  b.textContent = '💡';
  b.style.opacity = state.showTutor ? '1' : '0.4';
  b.style.filter = state.showTutor ? 'none' : 'grayscale(1)';
  b.title = state.showTutor ? '튜터 끄기' : '튜터 켜기';
  b.setAttribute('aria-label', state.showTutor ? '튜터 끄기' : '튜터 켜기');
  b.onclick = () => toggleTutor(); // set()이 재렌더 → 버튼이 새 상태로 다시 그려짐
  return b;
}
